import React, { useState, useEffect } from 'react';
import Header from '../components/landing/Header';
import TopBar from '../components/landing/TopBar';
import Footer from '../components/landing/Footer';
import EnquiryModal from '../components/landing/EnquiryModal';
import { Zap, Globe, Award, CheckCircle, TrendingUp } from 'lucide-react';
import api from '../utils/axios';
import getFullUrl from '../utils/formatUrl';

const Placements = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isInquiryOpen, setIsInquiryOpen] = useState(false);
    const [content, setContent] = useState({});

    useEffect(() => {
        window.scrollTo(0, 0);
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        fetchContent();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchContent = async () => {
        try {
            const res = await api.get('/content');
            const contentMap = {};
            res.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);
        } catch (err) {
            console.error("Failed to load web content", err);
        }
    };

    const stats = [
        { label: 'Highest Package', value: content.placements_highest || '1.2 Cr', icon: Zap },
        { label: 'Avg Package', value: content.placements_avg || '8.5 LPA', icon: TrendingUp },
        { label: 'Recruiters', value: content.placements_recruiters_count || '500+', icon: Globe },
        { label: 'Offers Made', value: content.placements_offers || '2500+', icon: Award }
    ];

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            {!isScrolled && <TopBar data={content} />}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => setIsInquiryOpen(true)}
                logo={content.site_logo}
                email={content.contact_email}
                phone={content.contact_phone}
                applyUrl={content.apply_url}
                colleges={content.colleges_list}
                placements={content.placements_list}
                research={content.research_list}
                journals={content.journals_list}
                innovations={content.innovations_list}
            />

            {/* Internal Hero */}
            <header className="relative h-[400px] bg-slate-900 flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-[#003366]/80 z-10"></div>
                <img 
                    src={getFullUrl(content.placements_hero_image) || "https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=2000&auto=format&fit=crop"} 
                    className="absolute inset-0 w-full h-full object-cover"
                    alt="Placements"
                />
                <div className="max-w-7xl mx-auto px-4 relative z-20 w-full text-center">
                    <h1 className="text-5xl md:text-6xl font-black text-white uppercase tracking-tighter">
                        Building <br /><span className="text-amber-400">Careers</span>
                    </h1>
                    <div className="h-1.5 w-24 bg-[#cc0000] mx-auto mt-6"></div>
                </div>
            </header>

            {/* Stats Grid */}
            <section className="bg-slate-50 py-20 -mt-10 relative z-30 mx-4 lg:mx-auto max-w-7xl shadow-2xl">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 px-10 border-b-8 border-amber-400">
                    {stats.map((s, i) => (
                        <div key={i} className="text-center group border-r last:border-0 border-slate-200">
                            <s.icon size={32} className="mx-auto text-[#cc0000] mb-4 group-hover:scale-110 transition-transform" />
                            <div className="text-3xl font-black text-slate-900 uppercase tracking-tighter mb-1">{s.value}</div>
                            <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Content Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex flex-col lg:flex-row gap-20 items-center">
                        <div className="lg:w-1/2">
                            <h2 className="text-4xl font-black uppercase tracking-tighter text-slate-900 mb-8">
                                {content.placements_title || 'Our Recruitment Excellence'}
                            </h2>
                            <p className="text-slate-600 leading-relaxed text-lg font-medium mb-10">
                                {content.placements_desc || 'The Training & Placement Cell at Arya Sabhyata acts as a crucial link between our talented students and the leading corporations. Our holistic approach includes soft-skills training, mock interviews, and technical bootcamps to ensure every student is career-ready.'}
                            </p>
                            <ul className="space-y-4">
                                {['Corporate Liaison Officers', 'Campus Recruitment Training', 'Alumni Network Support', '24/7 Digital Career Portal'].map((item, i) => (
                                    <li key={i} className="flex items-center space-x-3 text-sm font-bold uppercase tracking-wider text-slate-700">
                                        <CheckCircle className="text-[#cc0000]" size={20} />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="lg:w-1/2">
                            <img 
                                src={getFullUrl(content.placements_side_image) || "https://images.unsplash.com/photo-1552664688-cf412ec27db2?q=80&w=1000&auto=format&fit=crop"} 
                                className="rounded-2xl shadow-3xl"
                                alt="Placement Cell"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Recruiter Logos */}
            <section className="py-24 bg-slate-900 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4">
                    <h3 className="text-center text-white/30 font-black uppercase text-[10px] tracking-[8px] mb-16">Global Placement Partners</h3>
                    <div className="flex flex-wrap justify-center gap-12 md:gap-20 items-center">
                        {( () => {
                            try {
                                const recruiters = content.placements_recruiters ? JSON.parse(content.placements_recruiters) : [];
                                if (recruiters.length === 0) return ['TECH MAHINDRA', 'INFOSYS', 'TCS', 'WIPRO', 'COGNIZANT'].map((text, i) => <span key={i} className="text-xl font-black text-white/20">{text}</span>);
                                return recruiters.map((logo, i) => (
                                    <img 
                                        key={i} 
                                        src={getFullUrl(logo)} 
                                        className="h-8 md:h-12 w-auto object-contain filter brightness-0 invert opacity-40 hover:opacity-100 transition-all duration-500" 
                                        alt="Recruiter" 
                                    />
                                ));
                            } catch(e) {
                                return ['TECH MAHINDRA', 'INFOSYS', 'TCS', 'WIPRO', 'COGNIZANT'].map((text, i) => <span key={i} className="text-xl font-black text-white/20">{text}</span>);
                            }
                        })() }
                    </div>
                </div>
            </section>

            {/* Success Stories Section */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-20">
                        <h4 className="text-amber-500 font-black uppercase tracking-[5px] text-[10px] mb-4">Wall of Fame</h4>
                        <h2 className="text-5xl font-black text-slate-900 uppercase tracking-tighter">Student <span className="text-[#cc0000]">Success</span> Stories</h2>
                        <div className="h-1.5 w-24 bg-slate-900 mx-auto mt-6"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {(() => {
                            try {
                                const stories = content.placements_success_stories ? JSON.parse(content.placements_success_stories) : [];
                                if (stories.length === 0) return (
                                    <div className="col-span-full py-20 text-center bg-white border border-dashed border-slate-200 rounded-sm">
                                        <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No success stories added yet.</p>
                                    </div>
                                );
                                return stories.map((story, i) => (
                                    <div key={i} className="bg-white p-8 border border-slate-100 shadow-xl relative group hover:-translate-y-2 transition-all duration-500">
                                        <div className="absolute top-0 right-10 w-16 h-20 bg-[#cc0000] flex items-center justify-center text-white font-black text-lg shadow-lg">
                                            {story.package}<span className="text-[10px] ml-1">LPA</span>
                                        </div>
                                        <div className="flex items-center space-x-6 mb-8">
                                            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-slate-50 shadow-inner">
                                                <img src={getFullUrl(story.image) || "https://placehold.co/100x100?text=Student"} className="w-full h-full object-cover" alt={story.name} />
                                            </div>
                                            <div>
                                                <h4 className="text-lg font-black text-slate-900 uppercase tracking-tighter">{story.name}</h4>
                                                <p className="text-[10px] font-black text-[#cc0000] uppercase tracking-widest mt-1">{story.company}</p>
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <p className="text-slate-500 italic font-medium leading-relaxed relative z-10">"{story.text}"</p>
                                            <div className="absolute -top-4 -left-2 text-6xl text-slate-100 font-serif leading-none z-0 opacity-50">"</div>
                                        </div>
                                    </div>
                                ));
                            } catch(e) {
                                return null;
                            }
                        })()}
                    </div>
                </div>
            </section>

            <Footer data={content} />

            <EnquiryModal isOpen={isInquiryOpen} onClose={() => setIsInquiryOpen(false)} />
        </div>
    );
};

export default Placements;
